import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sendStreamingRequest, processStream, createSkeletonMessage } from "../services/ChatService.js";
import { startAudioPlayerWorklet } from "../lib/audio/audio-player.js";
import {
  startAudioRecorderWorklet,
  stopMicrophone,
} from "../lib/audio/audio-recorder.js";
import { cleanCJKSpaces, base64ToArrayBuffer, getWebSocketUrl } from "../lib/utils.js";
import { getOnboardingFieldOrder } from "../lib/constants/onboarding.js";

const createMessage = ({ id, role, content, isProcessing = false, metadata }) => ({
  id,
  role,
  content,
  timestamp: new Date(),
  isProcessing,
  metadata,
});

// Helper functions for onboarding
const getOnboardingQuestions = (onboardingQuestions) => {
  if (onboardingQuestions && onboardingQuestions.length > 0) {
    return onboardingQuestions;
  }
  return [];
};

const getOnboardingIntro = (collectionPrompt) => {
  return collectionPrompt || "Before we proceed, I need to collect some information.";
};

const validateOnboardingAnswer = (question, answer) => {
  if (!answer.trim()) {
    return { isValid: false, errorMessage: "This field is required." };
  }

  const fieldType = question.fieldType?.toLowerCase();

  // Email validation
  if (fieldType === "email") {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(answer)) {
      return {
        isValid: false,
        errorMessage: "Please enter a valid email address.",
      };
    }
  }

  // Phone validation
  if (fieldType === "phone") {
    const phoneRegex = /^[+]?[\d\s-]{10,15}$/;
    if (!phoneRegex.test(answer.replace(/\s/g, ""))) {
      return {
        isValid: false,
        errorMessage: "Please enter a valid phone number.",
      };
    }
  }

  // Name validation
  if (fieldType === "fullname") {
    if (answer.length > 50) {
      return {
        isValid: false,
        errorMessage: "Name must be less than 50 characters.",
      };
    }
  }

  return { isValid: true };
};

export const useChatState = ({
  welcomeMessage,
  quickQuestions = [],
  apiBaseUrl,
  apiKey,
  organizationId,
  autoOpen,
  openDelay,
  darkMode,
  enableVoiceInteraction = false,
  onboardingQuestions = [],
  onboardingEnabled = false,
  collectionPrompt,
}) => {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDark, setIsDark] = useState(darkMode);
  const [messages, setMessages] = useState(() => {
    if (!welcomeMessage) {
      return [];
    }
    return [
      { ...createMessage({ id: Date.now().toString(), role: "assistant", content: welcomeMessage }), isWelcome: true },
    ];
  });
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const widgetRef = useRef(null);
  const messageIdRef = useRef(1);

  // Onboarding state: show form first when onboarding is enabled (no message required)
  const [onboardingActive, setOnboardingActive] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [pendingUserIntent, setPendingUserIntent] = useState(null);
  const [onboardingAnswers, setOnboardingAnswers] = useState({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const pendingIntentProcessedRef = useRef(false);

  // Bidi/Voice state
  const [isVoiceSessionActive, setIsVoiceSessionActive] = useState(false);
  const [bidiMessages, setBidiMessages] = useState([]);
  const [voiceStatus, setVoiceStatus] = useState("idle"); // 'idle' | 'connecting' | 'connected' | 'error'
  const [voiceError, setVoiceError] = useState(null);
  const [bidiSessionId] = useState(
    () => `widget-session-${Math.random().toString(36).slice(2, 9)}`
  );

  // Bidi refs
  const websocketRef = useRef(null);
  const audioPlayerNodeRef = useRef(null);
  const audioPlayerContextRef = useRef(null);
  const audioRecorderNodeRef = useRef(null);
  const audioRecorderContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const currentInputMessageIdRef = useRef(null);
  const currentAssistantMessageIdRef = useRef(null);
  const isAudioReadyRef = useRef(false);
  const isVoiceSessionActiveRef = useRef(false);
  const speechSynthesisRef = useRef(null);
  const pendingAssistantUpdateRef = useRef(null); // Track pending assistant updates to prevent duplicates
  const userSpeechStartTimeRef = useRef(null); // Track when user started speaking for proper timestamp ordering
  const isProcessingAssistantUpdateRef = useRef(false); // Track if we're currently processing an assistant update
  const lastProcessedAssistantContentRef = useRef(null); // Track last processed content to prevent duplicates
  const userHasSpokenRef = useRef(false); // Track if user has sent first message - prevents automatic greetings

  const websocketUrl = useMemo(() => {
    if (!organizationId) return null;
    // Construct WebSocket URL from the API base URL
    const wsBaseUrl = getWebSocketUrl(apiBaseUrl);
    return `${wsBaseUrl}/${organizationId}/${bidiSessionId}`;
  }, [apiBaseUrl, organizationId, bidiSessionId]);

  const getNextMessageId = useCallback(() => {
    const id = `msg-${messageIdRef.current}`;
    messageIdRef.current += 1;
    return id;
  }, []);

  // Auto open with delay
  useEffect(() => {
    if (autoOpen && openDelay > 0) {
      const timer = setTimeout(() => setIsOpen(true), openDelay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoOpen, openDelay]);

  // Sync dark mode prop
  useEffect(() => {
    setIsDark(darkMode);
  }, [darkMode]);

  // When onboarding is enabled, show the form as the first screen (no message required)
  useEffect(() => {
    if (!onboardingEnabled || onboardingCompleted) return;
    const questions = getOnboardingQuestions(onboardingQuestions);
    if (questions.length > 0) {
      setOnboardingActive(true);
    }
  }, [onboardingEnabled, onboardingCompleted, onboardingQuestions]);

  // Sync welcome message updates
  useEffect(() => {
    if (!welcomeMessage) {
      return;
    }
    setMessages((prev) => {
      if (!prev.length) {
        return [
          { ...createMessage({ id: Date.now().toString(), role: "assistant", content: welcomeMessage }), isWelcome: true },
        ];
      }

      const [first, ...rest] = prev;
      if (first.isWelcome) {
        // Keep the same ID when updating welcome message content
        return [{ ...first, content: welcomeMessage }, ...rest];
      }

      return prev;
    });
  }, [welcomeMessage]);

  const appendAssistantMessage = useCallback((updater) => {
    setMessages((prev) =>
      prev.map((message) => {
        if (message.id === updater.id) {
          return { ...message, ...updater.update(message) };
        }
        return message;
      })
    );
  }, []);

  const sendMessage = useCallback(
    async (rawText, fieldType) => {
      const trimmed = rawText.trim();
      if (!trimmed) {
        return { success: false, error: 'Message cannot be empty' };
      }

      if (!apiBaseUrl) {
        setMessages((prev) => {
          const errorMessage = createMessage({
            id: getNextMessageId(),
            role: "assistant",
            content: "Sorry, I'm having trouble connecting right now. Please try again.",
            isProcessing: false,
          });
          return [...(prev || []), errorMessage];
        });
        console.error("Chat widget configuration error: apiBaseUrl is required for streaming.");
        return { success: false, error: 'API base URL is required' };
      }

      // Map fieldType to API format
      const validTypes = ['inquiry', 'complaint', 'technical', 'userName', 'email', 'phone', 'fullname', 'mobileno'];
      let apiType = "inquiry"; // default

      if (fieldType) {
        const lowerFieldType = fieldType.toLowerCase();
        if (lowerFieldType === 'fullname' || lowerFieldType === 'name') {
          apiType = 'userName';
        } else if (lowerFieldType === 'email') {
          apiType = 'email';
        } else if (lowerFieldType === 'phone' || lowerFieldType === 'mobileno') {
          apiType = 'phone';
        } else if (validTypes.includes(lowerFieldType)) {
          apiType = lowerFieldType;
        }
      }

      // Only add user message to UI for regular inquiries
      if (apiType === 'inquiry' || apiType === 'complaint' || apiType === 'technical') {
        const userMessage = createMessage({
          id: getNextMessageId(),
          role: "user",
          content: trimmed,
        });
        setMessages((prev) => [...(prev || []), userMessage]);
        setInputValue("");
        setIsLoading(true);
      } else {
        // For onboarding fields, clear input immediately
        setInputValue("");
      }

      try {
        // Prepare chat request
        const chatRequest = {
          message: trimmed,
          type: apiType,
          conversationId: bidiSessionId,
          messageHistory: messages
            .filter((msg) => {
              // Filter out processing messages, empty messages, and onboarding messages
              return !msg.isProcessing && msg.content && msg.content.trim() &&
                !['userName', 'email', 'phone'].includes(msg.type);
            })
            .map((msg) => {
              // Format timestamp to ISO string
              let timestamp;
              if (msg.timestamp instanceof Date) {
                timestamp = msg.timestamp.toISOString();
              } else if (typeof msg.timestamp === 'string') {
                timestamp = msg.timestamp;
              } else if (msg.timestamp) {
                timestamp = new Date(msg.timestamp).toISOString();
              } else {
                timestamp = new Date().toISOString();
              }

              return {
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: timestamp,
                isProcessing: false,
              };
            }),
        };

        // Send streaming request
        const stream = await sendStreamingRequest(chatRequest, apiBaseUrl, organizationId);

        // For onboarding fields, just consume the stream without processing
        if (apiType === 'userName' || apiType === 'email' || apiType === 'phone') {
          const reader = stream.getReader();
          const decoder = new TextDecoder();
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            decoder.decode(value);
          }
          return { success: true };
        }

        // For regular messages, process the stream
        if (apiType === 'inquiry' || apiType === 'complaint' || apiType === 'technical') {
          await processStream(stream, setMessages);
          return { success: true };
        }

        return { success: true };
      } catch (error) {
        console.error("Error sending message:", error);
        if (apiType === 'inquiry' || apiType === 'complaint' || apiType === 'technical') {
          setIsLoading(false);
          setMessages((prev) => {
            const filtered = (prev || []).filter((msg) => !msg.isProcessing);
            const errorMessage = createMessage({
              id: getNextMessageId(),
              role: "assistant",
              content: "Sorry, I'm having trouble connecting right now. Please try again.",
              isProcessing: false,
            });
            return [...filtered, errorMessage];
          });
        }
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to send message'
        };
      } finally {
        if (apiType === 'inquiry' || apiType === 'complaint' || apiType === 'technical') {
          setIsLoading(false);
        }
      }
    },
    [apiBaseUrl, apiKey, getNextMessageId, organizationId, messages, bidiSessionId]
  );

  const startOnboarding = useCallback(() => {
    const questions = getOnboardingQuestions(onboardingQuestions);
    if (questions.length === 0) {
      return;
    }
    setOnboardingActive(true);
    setCurrentQuestionIndex(0);
    // Form UI shows all questions at once; no intro/first-question messages
  }, [onboardingQuestions]);

  const handleOnboardingFormSubmit = useCallback(
    async (answers) => {
      const questions = getOnboardingQuestions(onboardingQuestions);
      if (questions.length === 0) return;

      const sorted = [...questions].sort(
        (a, b) =>
          getOnboardingFieldOrder(a.fieldType) - getOnboardingFieldOrder(b.fieldType)
      );

      for (const q of sorted) {
        const key = q.fieldType || "question";
        const value = (answers[key] ?? "").trim();
        if (!value) continue;
        const res = await sendMessage(value, key);
        if (!res?.success) {
          const errorMsg = createMessage({
            id: getNextMessageId(),
            role: "assistant",
            content: res?.error || "Something went wrong. Please try again.",
            isProcessing: false,
          });
          setMessages((prev) => [...prev, errorMsg]);
          return;
        }
      }

      setOnboardingActive(false);
      setOnboardingCompleted(true);
      setOnboardingAnswers((prev) => ({ ...prev, ...answers }));

      // Confirmation message disabled - no need to show after onboarding
      // const successMessage = createMessage({
      //   id: getNextMessageId(),
      //   role: "assistant",
      //   content: "Thank you! I've received your information.",
      //   isProcessing: false,
      // });
      // setMessages((prev) => [...prev, successMessage]);

      if (pendingUserIntent && !pendingIntentProcessedRef.current) {
        pendingIntentProcessedRef.current = true;
        setTimeout(() => {
          setInputValue(pendingUserIntent);
          setPendingUserIntent(null);
        }, 500);
      }
    },
    [
      onboardingQuestions,
      sendMessage,
      getNextMessageId,
      pendingUserIntent,
    ]
  );

  const handleOnboardingAnswer = useCallback(
    async (answer) => {
      if (isProcessingAnswer) {
        return;
      }

      const questions = getOnboardingQuestions(onboardingQuestions);
      const currentQuestion = questions[currentQuestionIndex];

      if (!currentQuestion) {
        console.error("No question found at index", currentQuestionIndex);
        return;
      }

      // Validate answer first
      const validation = validateOnboardingAnswer(currentQuestion, answer);
      if (!validation.isValid) {
        const errorMessage = createMessage({
          id: getNextMessageId(),
          role: "assistant",
          content: validation.errorMessage || "Invalid input. Please try again.",
          isProcessing: false,
        });
        setMessages((prev) => [...prev, errorMessage]);
        return;
      }

      setIsProcessingAnswer(true);

      const fieldType = currentQuestion.fieldType || `question_${currentQuestionIndex}`;

      // Add user message to UI
      const userAnswer = createMessage({
        id: getNextMessageId(),
        role: "user",
        content: answer,
        timestamp: new Date(),
        isProcessing: false,
      });
      setMessages((prev) => [...prev, userAnswer]);

      // Save answer locally
      setOnboardingAnswers((prev) => ({
        ...prev,
        [fieldType]: answer,
      }));

      try {
        // Send to API
        const response = await sendMessage(answer, fieldType);

        if (!response || !response.success) {
          const errorMsg = createMessage({
            id: getNextMessageId(),
            role: "assistant",
            content: response?.error || "Failed to save your answer. Please try again.",
            isProcessing: false,
          });
          setMessages((prev) => [...prev, errorMsg]);
          setIsProcessingAnswer(false);
          return;
        }

        const nextIndex = currentQuestionIndex + 1;

        if (nextIndex < questions.length) {
          setCurrentQuestionIndex(nextIndex);
          setIsProcessingAnswer(false);

          setTimeout(() => {
            const nextQuestion = questions[nextIndex];
            const questionMessage = createMessage({
              id: getNextMessageId(),
              role: "assistant",
              content: nextQuestion.askToType ?? nextQuestion,
              isProcessing: false,
            });
            setMessages((prev) => [...prev, questionMessage]);
          }, 300);
        } else {
          // Onboarding completed
          setOnboardingActive(false);
          setOnboardingCompleted(true);
          setIsProcessingAnswer(false);

          setTimeout(() => {
            // Confirmation message disabled - no need to show after onboarding
            // const successMessage = createMessage({
            //   id: getNextMessageId(),
            //   role: "assistant",
            //   content: "Thank you! I've received your information.",
            //   isProcessing: false,
            // });
            // setMessages((prev) => [...prev, successMessage]);

            // If there's a pending user intent, pre-fill input
            if (pendingUserIntent && !pendingIntentProcessedRef.current) {
              pendingIntentProcessedRef.current = true;
              setTimeout(() => {
                setInputValue(pendingUserIntent);
                setPendingUserIntent(null);
              }, 500);
            }
          }, 300);
        }
      } catch (error) {
        const errorMsg = createMessage({
          id: getNextMessageId(),
          role: "assistant",
          content: "An error occurred. Please try again.",
          isProcessing: false,
        });
        setMessages((prev) => [...prev, errorMsg]);
        setIsProcessingAnswer(false);
      }
    },
    [
      onboardingQuestions,
      currentQuestionIndex,
      pendingUserIntent,
      sendMessage,
      isProcessingAnswer,
      getNextMessageId,
    ]
  );

  const handleSmartSubmit = useCallback(
    async () => {
      const questions = getOnboardingQuestions(onboardingQuestions);
      const trimmedInput = inputValue.trim();

      if (!trimmedInput) {
        return;
      }

      // Case 1: No onboarding questions OR onboarding already completed OR onboarding disabled
      if (questions.length === 0 || onboardingCompleted || !onboardingEnabled) {
        await sendMessage(trimmedInput);
        return;
      }

      // Case 2: User's FIRST interaction - start onboarding
      if (!hasUserInteracted && !onboardingActive) {
        setHasUserInteracted(true);
        pendingIntentProcessedRef.current = false;

        // Add user message to chat immediately
        const userMessage = createMessage({
          id: getNextMessageId(),
          role: "user",
          content: trimmedInput,
          timestamp: new Date(),
          isProcessing: false,
        });
        setMessages((prev) => [...prev, userMessage]);

        // Store the user's intent
        setPendingUserIntent(trimmedInput);
        setInputValue("");
        startOnboarding();
        return;
      }

      // Case 3: Onboarding is ACTIVE - handle answer
      if (onboardingActive) {
        await handleOnboardingAnswer(trimmedInput);
        setInputValue("");
        return;
      }

      // Default: send as regular message
      await sendMessage(trimmedInput);
    },
    [
      onboardingQuestions,
      inputValue,
      onboardingActive,
      onboardingCompleted,
      hasUserInteracted,
      startOnboarding,
      handleOnboardingAnswer,
      sendMessage,
      getNextMessageId,
    ]
  );

  const handleSend = useCallback(async () => {
    await handleSmartSubmit();
  }, [handleSmartSubmit]);

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    setIsMinimized(false);
  };

  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  const handleQuickQuestion = useCallback(
    async (question) => {
      const trimmedQuestion = question?.trim();
      if (!trimmedQuestion) {
        return;
      }

      const questions = getOnboardingQuestions(onboardingQuestions);

      // Case 1: No onboarding questions OR onboarding already completed OR onboarding disabled
      if (questions.length === 0 || onboardingCompleted || !onboardingEnabled) {
        await sendMessage(trimmedQuestion);
        return;
      }

      // Case 2: User's FIRST interaction - start onboarding
      if (!hasUserInteracted && !onboardingActive) {
        setHasUserInteracted(true);
        pendingIntentProcessedRef.current = false;

        // Add user message to chat immediately
        const userMessage = createMessage({
          id: getNextMessageId(),
          role: "user",
          content: trimmedQuestion,
          timestamp: new Date(),
          isProcessing: false,
        });
        setMessages((prev) => [...prev, userMessage]);

        // Store the user's intent
        setPendingUserIntent(trimmedQuestion);
        startOnboarding();
        return;
      }

      // Case 3: Onboarding is ACTIVE - handle answer
      if (onboardingActive) {
        await handleOnboardingAnswer(trimmedQuestion);
        return;
      }

      // Default: send as regular message
      await sendMessage(trimmedQuestion);
    },
    [
      onboardingQuestions,
      onboardingActive,
      onboardingCompleted,
      hasUserInteracted,
      startOnboarding,
      handleOnboardingAnswer,
      sendMessage,
      getNextMessageId,
    ]
  );

  /**
   * Handle user decision for HITL (Human-in-the-Loop)
   */
  const handleDecision = useCallback(
    async (decisionId, optionId, optionValue) => {
      // Remove the decision message from the list
      setMessages((prev) =>
        prev.filter((msg) => msg.decisionData?.decisionId !== decisionId)
      );

      // Handle the decision based on the selected option
      if (optionValue === "connect_agent") {
        // User wants to connect with a human agent
        const responseMessage = createMessage({
          id: getNextMessageId(),
          role: "assistant",
          content:
            "Great! I'm setting up a connection with a live customer service agent. Please hold on for a moment while I transfer you.",
        });
        setMessages((prev) => [...prev, responseMessage]);
      } else if (optionValue === "decline") {
        // User declined to connect with a human agent
        const responseMessage = createMessage({
          id: getNextMessageId(),
          role: "assistant",
          content:
            "I understand. I apologize that I don't have specific information about your query in my knowledge base. Is there anything else I can help you with?",
        });
        setMessages((prev) => [...prev, responseMessage]);
      }
    },
    [getNextMessageId]
  );

  // Bidi/Voice functions
  const finalizePendingInputMessage = useCallback(() => {
    if (!currentInputMessageIdRef.current) {
      return;
    }
    console.log("[BIDI] finalizePendingInputMessage called", { messageId: currentInputMessageIdRef.current });
    setBidiMessages((prev) => {
      const updated = prev.map((message) =>
        message.id === currentInputMessageIdRef.current
          ? { ...message, isProcessing: false }
          : message
      );
      console.log("[BIDI] finalizePendingInputMessage: updated state", {
        messageId: currentInputMessageIdRef.current,
        found: prev.some(m => m.id === currentInputMessageIdRef.current),
        prevLength: prev.length,
        newLength: updated.length
      });
      return updated;
    });
    currentInputMessageIdRef.current = null;
  }, []);

  const appendUserTranscription = useCallback(
    (text, finished = false) => {
      console.log("[BIDI] appendUserTranscription called", {
        text: text?.substring(0, 50),
        finished,
        currentInputId: currentInputMessageIdRef.current
      });

      // Mark that user has spoken - now we can process assistant responses
      userHasSpokenRef.current = true;

      if (!text || (typeof text === 'string' && !text.trim())) {
        console.log("[BIDI] appendUserTranscription: skipping empty text");
        if (finished && currentInputMessageIdRef.current) {
          setBidiMessages((prev) =>
            prev.map((message) =>
              message.id === currentInputMessageIdRef.current
                ? { ...message, isProcessing: false }
                : message
            )
          );
          currentInputMessageIdRef.current = null;
        }
        return;
      }

      const chunk = cleanCJKSpaces(text);
      setBidiMessages((prev) => {
        const prevLength = prev.length;
        let result = [...prev]; // Create new array to avoid mutations

        if (!currentInputMessageIdRef.current) {
          const newId = `bidi-input-${Date.now()}`;
          // CRITICAL: Use a timestamp that's BEFORE any existing assistant messages from this turn
          // Find the most recent assistant message and use a timestamp before it
          const now = Date.now();
          const latestAssistantMessage = prev
            .filter(m => m.role === "assistant" && m.metadata?.source === "bidi")
            .sort((a, b) => {
              const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
              const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
              return timeB - timeA; // Sort descending to get most recent
            })[0];

          let userTimestamp;
          if (latestAssistantMessage) {
            const assistantTime = latestAssistantMessage.timestamp instanceof Date
              ? latestAssistantMessage.timestamp.getTime()
              : new Date(latestAssistantMessage.timestamp).getTime();
            // Use timestamp 1ms before the assistant message to ensure user appears first
            userTimestamp = new Date(assistantTime - 1);
            console.log("[BIDI] appendUserTranscription: using timestamp before assistant message", {
              assistantTime: new Date(assistantTime).toISOString(),
              userTime: userTimestamp.toISOString(),
              assistantId: latestAssistantMessage.id
            });
          } else {
            // No assistant message yet, use current time
            userTimestamp = new Date(now);
            console.log("[BIDI] appendUserTranscription: no assistant message, using current time", {
              userTime: userTimestamp.toISOString()
            });
          }

          const userMessage = createMessage({
            id: newId,
            role: "user",
            content: chunk,
            isProcessing: !finished,
            timestamp: userTimestamp, // Ensure user message timestamp is before assistant
            metadata: { source: "bidi", transcriptionType: "input" },
          });

          // Check if message already exists (prevent duplicates)
          const messageExists = result.some(m => m.id === newId);
          if (!messageExists) {
            result = [...result, userMessage];
            // Set ref AFTER adding message to result array
            currentInputMessageIdRef.current = newId;
            console.log("[BIDI] appendUserTranscription: created new user message", {
              messageId: newId,
              content: chunk.substring(0, 50),
              prevLength,
              newLength: result.length,
              allMessageIds: result.map(m => m.id)
            });
          } else {
            console.warn("[BIDI] appendUserTranscription: message already exists, updating instead", { messageId: newId });
            currentInputMessageIdRef.current = newId;
            result = result.map((msg) =>
              msg.id === newId
                ? { ...msg, content: `${msg.content || ""}${chunk}`, isProcessing: !finished }
                : msg
            );
          }
        } else {
          // Update existing user message
          const userMessageExists = result.some(m => m.id === currentInputMessageIdRef.current);
          if (userMessageExists) {
            result = result.map((message) => {
              if (message.id !== currentInputMessageIdRef.current) {
                return message;
              }
              const combined = cleanCJKSpaces(`${message.content || ""}${chunk}`);
              console.log("[BIDI] appendUserTranscription: updating existing message", {
                messageId: message.id,
                oldContent: message.content?.substring(0, 30),
                newContent: combined.substring(0, 50),
                oldLength: message.content?.length || 0,
                newLength: combined.length
              });
              return {
                ...message,
                content: combined,
                // CRITICAL: Don't update timestamp when appending content - preserve original timestamp for ordering
                // timestamp: new Date(), // Removed - preserve original timestamp
                isProcessing: !finished,
                metadata: { ...message.metadata, transcriptionType: "input" },
              };
            });
          } else {
            console.error("[BIDI] appendUserTranscription: ERROR - user message not found!", {
              expectedId: currentInputMessageIdRef.current,
              existingIds: result.map(m => m.id)
            });
            // Message was lost - create a new one
            const newId = `bidi-input-${Date.now()}`;
            currentInputMessageIdRef.current = newId;
            const userMessage = createMessage({
              id: newId,
              role: "user",
              content: chunk,
              isProcessing: !finished,
              metadata: { source: "bidi", transcriptionType: "input" },
            });
            result = [...result, userMessage];
            console.log("[BIDI] appendUserTranscription: recreated lost user message", { messageId: newId });
          }
        }

        console.log("[BIDI] appendUserTranscription: state update complete", {
          prevLength,
          newLength: result.length,
          messageIds: result.map(m => m.id),
          messageRoles: result.map(m => m.role)
        });

        // CRITICAL: Safety check - never return fewer messages than we started with
        if (result.length < prevLength) {
          console.error("[BIDI] appendUserTranscription: CRITICAL ERROR - losing messages!", {
            prevLength,
            resultLength: result.length,
            prevIds: prev.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 30) })),
            resultIds: result.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 30) }))
          });
          return prev; // Return previous state to prevent data loss
        }

        return result;
      });

      if (finished) {
        console.log("[BIDI] appendUserTranscription: finalizing message", { messageId: currentInputMessageIdRef.current });
        currentInputMessageIdRef.current = null;
        setIsLoading(true);
      }
    },
    []
  );

  const appendAssistantContent = useCallback(
    (text, finished = false) => {
      setIsLoading(false);
      console.log("[BIDI] appendAssistantContent called", {
        text: text?.substring(0, 50),
        finished,
        currentAssistantId: currentAssistantMessageIdRef.current,
        currentInputId: currentInputMessageIdRef.current
      });

      if (!text || (typeof text === 'string' && !text.trim())) {
        console.log("[BIDI] appendAssistantContent: skipping empty text");
        if (finished && currentAssistantMessageIdRef.current) {
          setBidiMessages((prev) =>
            prev.map((message) => {
              if (message.id !== currentAssistantMessageIdRef.current) {
                return message;
              }
              return { ...message, isProcessing: false };
            })
          );
          currentAssistantMessageIdRef.current = null;
          pendingAssistantUpdateRef.current = null;
        }
        return;
      }

      // CRITICAL: Prevent duplicate updates from rapid calls
      // Use a simple content-based key to detect exact duplicates
      const contentKey = `${currentAssistantMessageIdRef.current || 'new'}-${text.substring(0, 100)}-${finished}`;

      // If we're already processing an update, skip this one
      if (isProcessingAssistantUpdateRef.current) {
        // Check if this is the exact same content we're already processing
        if (lastProcessedAssistantContentRef.current === contentKey) {
          console.log("[BIDI] appendAssistantContent: skipping duplicate - already processing", { contentKey });
          return;
        }
        // If it's different content, we need to wait - but for now, let it through
        // The state update will handle it correctly
      }

      // Mark that we're processing an update
      isProcessingAssistantUpdateRef.current = true;
      lastProcessedAssistantContentRef.current = contentKey;

      // CRITICAL: Combine finalizePendingInputMessage and appendAssistantContent into ONE atomic update
      // This prevents race conditions where React batches separate updates
      const inputMessageIdToFinalize = currentInputMessageIdRef.current;

      // Use functional update to ensure we always work with the latest state
      setBidiMessages((prev) => {
        const prevLength = prev.length;
        let result = [...prev]; // Create new array to avoid mutations

        // CRITICAL: Verify we have the expected messages before proceeding
        console.log("[BIDI] appendAssistantContent: starting update", {
          prevLength,
          prevMessageIds: prev.map(m => m.id),
          inputMessageIdToFinalize,
          currentAssistantId: currentAssistantMessageIdRef.current,
          textLength: text.length
        });

        // First, finalize pending input message if needed (atomic with assistant update)
        if (inputMessageIdToFinalize) {
          const inputMessageExists = result.some(m => m.id === inputMessageIdToFinalize);
          if (inputMessageExists) {
            result = result.map((message) =>
              message.id === inputMessageIdToFinalize
                ? { ...message, isProcessing: false }
                : message
            );
            console.log("[BIDI] appendAssistantContent: finalized input message atomically", {
              inputMessageId: inputMessageIdToFinalize
            });
          } else {
            console.warn("[BIDI] appendAssistantContent: input message not found for finalization", {
              inputMessageId: inputMessageIdToFinalize,
              existingIds: result.map(m => m.id)
            });
          }
        }

        // Then, handle assistant content in the same update
        if (!currentAssistantMessageIdRef.current) {
          const newId = `bidi-assistant-${Date.now()}`;
          // CRITICAL: Ensure assistant message timestamp allows user message to be inserted before it
          // If there's a pending user message, use current time. Otherwise, use a timestamp that's
          // far enough in the future that a user message arriving later can be inserted before it
          const now = Date.now();
          const assistantTimestamp = inputMessageIdToFinalize
            ? new Date(now) // User message exists, use current time
            : new Date(now + 1000); // No user message yet, use 1 second in future so user message can be inserted before
          const assistantMessage = createMessage({
            id: newId,
            role: "assistant",
            content: text,
            isProcessing: !finished,
            timestamp: assistantTimestamp, // Ensure proper ordering
            metadata: { source: "bidi" },
          });

          // CRITICAL: Double-check message doesn't already exist
          const messageExists = result.some(m => m.id === newId);
          if (!messageExists) {
            result = [...result, assistantMessage];
            // Set ref AFTER adding message to result array to ensure consistency
            currentAssistantMessageIdRef.current = newId;
            console.log("[BIDI] appendAssistantContent: created new assistant message", {
              messageId: newId,
              content: text.substring(0, 100),
              prevLength,
              newLength: result.length,
              allMessageIds: result.map(m => m.id)
            });
          } else {
            console.error("[BIDI] appendAssistantContent: ERROR - message ID collision!", {
              messageId: newId,
              existingIds: result.map(m => m.id)
            });
            // Update existing message instead of creating duplicate
            result = result.map((msg) =>
              msg.id === newId
                ? { ...msg, content: `${msg.content || ""}${text}`, isProcessing: !finished }
                : msg
            );
            // Set ref to the existing message
            currentAssistantMessageIdRef.current = newId;
          }
        } else {
          // Update existing assistant message
          const assistantMessageExists = result.some(m => m.id === currentAssistantMessageIdRef.current);
          if (assistantMessageExists) {
            result = result.map((message) => {
              if (message.id !== currentAssistantMessageIdRef.current) {
                return message;
              }
              const newContent = `${message.content || ""}${text}`;
              console.log("[BIDI] appendAssistantContent: updating existing message", {
                messageId: message.id,
                oldContentLength: message.content?.length || 0,
                newContentLength: newContent.length,
                textLength: text.length
              });
              return {
                ...message,
                content: newContent,
                // CRITICAL: Don't update timestamp when appending content - preserve original timestamp for ordering
                // timestamp: new Date(), // Removed - preserve original timestamp
                isProcessing: !finished,
              };
            });
          } else {
            console.error("[BIDI] appendAssistantContent: ERROR - assistant message not found!", {
              expectedId: currentAssistantMessageIdRef.current,
              existingIds: result.map(m => m.id)
            });
            // Message was lost - create a new one
            const newId = `bidi-assistant-${Date.now()}`;
            currentAssistantMessageIdRef.current = newId;
            const assistantMessage = createMessage({
              id: newId,
              role: "assistant",
              content: text,
              isProcessing: !finished,
              metadata: { source: "bidi" },
            });
            result = [...result, assistantMessage];
            console.log("[BIDI] appendAssistantContent: recreated lost assistant message", { messageId: newId });
          }
        }

        console.log("[BIDI] appendAssistantContent: atomic state update complete", {
          prevLength,
          newLength: result.length,
          finished,
          messageIds: result.map(m => m.id),
          messageRoles: result.map(m => m.role)
        });

        // CRITICAL: Safety check - never return fewer messages than we started with
        if (result.length < prevLength) {
          console.error("[BIDI] appendAssistantContent: CRITICAL ERROR - losing messages!", {
            prevLength,
            resultLength: result.length,
            prevIds: prev.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 30) })),
            resultIds: result.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 30) }))
          });
          // Return previous state to prevent data loss
          return prev;
        }

        return result;
      });

      // Clear the input message ref after state update
      if (inputMessageIdToFinalize) {
        currentInputMessageIdRef.current = null;
      }

      if (finished) {
        console.log("[BIDI] appendAssistantContent: finalizing message", { messageId: currentAssistantMessageIdRef.current });
        currentAssistantMessageIdRef.current = null;
        pendingAssistantUpdateRef.current = null;
      } else {
        // Clear pending update ref after state update completes to allow new updates
        // Use a microtask to clear after React has processed the update
        const keyToClear = updateKey;
        Promise.resolve().then(() => {
          if (pendingAssistantUpdateRef.current === keyToClear) {
            pendingAssistantUpdateRef.current = null;
          }
        });
      }
    },
    []
  );

  const handleContentParts = useCallback(
    (parts, finished = false) => {
      console.log("[BIDI] handleContentParts called", { partsCount: parts?.length, finished });

      if (!parts || !Array.isArray(parts)) {
        console.warn("[BIDI] handleContentParts: invalid parts", parts);
        return;
      }

      // CRITICAL FIX: Accumulate all text parts and call appendAssistantContent ONCE
      // This prevents multiple rapid setBidiMessages calls that React batches together
      let accumulatedText = '';

      parts.forEach((part, index) => {
        if (
          part.inlineData?.data &&
          part.inlineData.mimeType?.startsWith("audio/pcm")
        ) {
          console.log("[BIDI] handleContentParts: sending audio data to player");
          if (audioPlayerNodeRef.current) {
            audioPlayerNodeRef.current.port.postMessage(
              base64ToArrayBuffer(part.inlineData.data)
            );
          } else {
            console.warn("[BIDI] handleContentParts: audioPlayerNodeRef is null");
          }
        }
        if (part.text) {
          accumulatedText += part.text;
          console.log("[BIDI] handleContentParts: accumulated text part", {
            text: part.text.substring(0, 50),
            index,
            totalAccumulated: accumulatedText.length
          });
        }
      });

      // Call appendAssistantContent ONCE with all accumulated text
      if (accumulatedText) {
        console.log("[BIDI] handleContentParts: calling appendAssistantContent once with accumulated text", {
          textLength: accumulatedText.length,
          finished
        });
        appendAssistantContent(accumulatedText, finished);
      }
    },
    [appendAssistantContent]
  );

  const handleTurnComplete = useCallback(() => {
    setIsLoading(false);
    console.log("[BIDI] handleTurnComplete called", {
      currentAssistantId: currentAssistantMessageIdRef.current,
      currentInputId: currentInputMessageIdRef.current
    });

    setBidiMessages((prev) => {
      const updated = prev.map((message) =>
        message.metadata?.source === "bidi" && message.isProcessing
          ? { ...message, isProcessing: false }
          : message
      );
      console.log("[BIDI] handleTurnComplete: state update", {
        prevLength: prev.length,
        newLength: updated.length,
        finalizedCount: prev.filter(m => m.metadata?.source === "bidi" && m.isProcessing).length
      });
      return updated;
    });

    currentAssistantMessageIdRef.current = null;
    currentInputMessageIdRef.current = null;
  }, []);

  const handleInterrupted = useCallback(() => {
    setIsLoading(false);
    if (audioPlayerNodeRef.current) {
      audioPlayerNodeRef.current.port.postMessage({ command: "endOfAudio" });
    }
    currentAssistantMessageIdRef.current = null;
    currentInputMessageIdRef.current = null;
    setBidiMessages((prev) =>
      prev.map((message) =>
        message.metadata?.source === "bidi"
          ? {
            ...message,
            isProcessing: false,
            metadata: { ...message.metadata, interrupted: true },
          }
          : message
      )
    );
  }, []);

  const handleBidiEvent = useCallback(
    (event) => {
      console.log("[BIDI] handleBidiEvent called", {
        event,
        isVoiceSessionActive: isVoiceSessionActiveRef.current,
        hasTurnComplete: !!event?.turnComplete,
        hasInterrupted: !!event?.interrupted,
        hasInputTranscription: !!event?.inputTranscription,
        hasOutputTranscription: !!event?.outputTranscription,
        hasContentParts: !!event?.content?.parts
      });

      if (!event) {
        console.warn("[BIDI] handleBidiEvent: received null/undefined event");
        return;
      }

      if (event.turnComplete) {
        console.log("[BIDI] handleBidiEvent: processing turnComplete");
        handleTurnComplete();
        return;
      }
      if (event.interrupted) {
        console.log("[BIDI] handleBidiEvent: processing interrupted");
        handleInterrupted();
        return;
      }
      // Always process user transcriptions - they mark when user has spoken
      if (event.inputTranscription) {
        const transcriptionText = event.inputTranscription.text || '';
        const transcriptionFinished = Boolean(event.inputTranscription.finished);
        console.log("[BIDI] handleBidiEvent: processing inputTranscription", {
          text: transcriptionText.substring(0, 50),
          textLength: transcriptionText.length,
          finished: transcriptionFinished,
          hasText: !!transcriptionText
        });

        // Only call appendUserTranscription if there's actual text or if it's finished
        if (transcriptionText || transcriptionFinished) {
          appendUserTranscription(transcriptionText, transcriptionFinished);
        } else {
          console.warn("[BIDI] handleBidiEvent: inputTranscription event has no text and not finished", event.inputTranscription);
        }
      }

      // CRITICAL: Ignore assistant responses until user has spoken
      // This prevents automatic greetings from appearing before user's first message
      if (!userHasSpokenRef.current) {
        // Silently ignore assistant responses before user speaks
        if (event.outputTranscription?.text || event.content?.parts) {
          console.log("[BIDI] handleBidiEvent: ignoring assistant response - user hasn't spoken yet", {
            hasOutputTranscription: !!event.outputTranscription?.text,
            hasContentParts: !!event.content?.parts
          });
          return;
        }
      }

      if (event.outputTranscription?.text) {
        console.log("[BIDI] handleBidiEvent: processing outputTranscription", {
          text: event.outputTranscription.text.substring(0, 50),
          finished: event.outputTranscription.finished
        });
        appendAssistantContent(
          event.outputTranscription.text,
          Boolean(event.outputTranscription.finished)
        );
      }
      if (event.content?.parts) {
        // Check if the content has a finished flag, or if the event itself indicates completion
        const contentFinished = event.content?.finished || event.finished || false;
        console.log("[BIDI] handleBidiEvent: processing content parts", {
          partsCount: event.content.parts.length,
          contentFinished,
          eventFinished: event.finished
        });
        handleContentParts(event.content.parts, contentFinished);
      }

      // Log if no handlers processed the event
      if (!event.turnComplete && !event.interrupted && !event.inputTranscription && !event.outputTranscription && !event.content) {
        console.warn("[BIDI] handleBidiEvent: event had no recognized fields", event);
      }
    },
    [
      appendAssistantContent,
      appendUserTranscription,
      handleContentParts,
      handleInterrupted,
      handleTurnComplete,
    ]
  );

  const handleRecorderAudio = useCallback(
    (pcmData) => {
      const ws = websocketRef.current;
      if (
        !isVoiceSessionActiveRef.current ||
        !ws ||
        ws.readyState !== WebSocket.OPEN
      ) {
        return;
      }
      ws.send(pcmData);
    },
    []
  );

  const initializeAudioPipeline = useCallback(async () => {
    if (isAudioReadyRef.current || typeof window === "undefined") {
      return;
    }
    try {
      const [playerNode, playerContext] = await startAudioPlayerWorklet();
      audioPlayerNodeRef.current = playerNode;
      audioPlayerContextRef.current = playerContext;
      await playerContext.resume();

      const [recorderNode, recorderContext, micStream] =
        await startAudioRecorderWorklet(handleRecorderAudio);
      audioRecorderNodeRef.current = recorderNode;
      audioRecorderContextRef.current = recorderContext;
      micStreamRef.current = micStream;
      await recorderContext.resume();

      isAudioReadyRef.current = true;
    } catch (error) {
      console.error("Failed to initialize audio pipeline", error);
      throw error;
    }
  }, [handleRecorderAudio]);

  const connectWebsocket = useCallback(() => {
    if (typeof window === "undefined" || !websocketUrl) {
      return;
    }
    if (
      websocketRef.current &&
      websocketRef.current.readyState === WebSocket.OPEN
    ) {
      return;
    }

    setVoiceStatus("connecting");
    setVoiceError(null);
    try {
      const ws = new WebSocket(websocketUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        setVoiceStatus("connected");
      };

      ws.onmessage = (event) => {
        try {
          const dataPreview = typeof event.data === 'string' ? event.data.substring(0, 200) : 'binary';
          console.log("[BIDI] WebSocket message received", {
            dataLength: event.data?.length,
            dataPreview
          });
          const payload = JSON.parse(event.data);
          console.log("[BIDI] WebSocket message parsed", {
            hasTurnComplete: !!payload.turnComplete,
            hasInterrupted: !!payload.interrupted,
            hasInputTranscription: !!payload.inputTranscription,
            hasOutputTranscription: !!payload.outputTranscription,
            hasContentParts: !!payload.content?.parts,
            payload
          });
          handleBidiEvent(payload);
        } catch (error) {
          console.error("[BIDI] Failed to parse WebSocket event", { error, data: event.data });
        }
      };

      ws.onerror = () => {
        setVoiceStatus("error");
        setVoiceError("Voice session connection error");
        setIsLoading(false);
      };

      ws.onclose = () => {
        websocketRef.current = null;
        setVoiceStatus("idle");
        setIsLoading(false);
        if (isVoiceSessionActiveRef.current) {
          setTimeout(() => {
            connectWebsocket();
          }, 5000);
        }
      };
    } catch (error) {
      console.error("Failed to open voice websocket", error);
      setVoiceStatus("error");
      setVoiceError(
        error instanceof Error
          ? error.message
          : "Unable to start voice session"
      );
    }
  }, [handleBidiEvent, websocketUrl]);

  const startVoiceSession = useCallback(async () => {
    if (!enableVoiceInteraction || !organizationId) {
      return;
    }
    setVoiceError(null);
    setVoiceStatus((prev) => (prev === "connected" ? prev : "connecting"));
    setIsVoiceSessionActive(true);
    isVoiceSessionActiveRef.current = true;

    // Reset userHasSpoken flag when starting new session
    userHasSpokenRef.current = false;

    setBidiMessages((prev) => {
      // Don't add welcome message if there are already any messages (either bidi or text)
      // This prevents showing welcome message when turning voice on again after a conversation
      const hasExistingMessages = prev.length > 0 || messages.length > 0;
      if (hasExistingMessages) {
        return prev;
      }

      // Only add welcome message if there are no messages at all
      if (!welcomeMessage || welcomeMessage.trim() === "") {
        return [];
      }
      const welcomeMsg = createMessage({
        id: `bidi-welcome-${Date.now()}`,
        role: "assistant",
        content: welcomeMessage,
        metadata: { source: "bidi" },
      });
      return [welcomeMsg];
    });

    connectWebsocket();

    try {
      await initializeAudioPipeline();
    } catch (error) {
      console.error("Failed to start audio pipeline", error);
      setVoiceError(
        error instanceof Error
          ? error.message
          : "Unable to access microphone"
      );
      setVoiceStatus("error");
    }
  }, [
    enableVoiceInteraction,
    organizationId,
    welcomeMessage,
    connectWebsocket,
    initializeAudioPipeline,
    messages,
  ]);

  const stopVoiceSession = useCallback(() => {
    setIsVoiceSessionActive(false);
    setIsLoading(false);
    isVoiceSessionActiveRef.current = false;
    setVoiceStatus("idle");
    setVoiceError(null);

    // Reset userHasSpoken flag when stopping session
    userHasSpokenRef.current = false;

    if (
      websocketRef.current &&
      websocketRef.current.readyState === WebSocket.OPEN
    ) {
      websocketRef.current.close();
    }
    websocketRef.current = null;

    audioPlayerNodeRef.current?.disconnect();
    audioPlayerContextRef.current?.close();
    audioPlayerNodeRef.current = null;
    audioPlayerContextRef.current = null;

    audioRecorderNodeRef.current?.disconnect();
    audioRecorderContextRef.current?.close();
    audioRecorderNodeRef.current = null;
    audioRecorderContextRef.current = null;

    stopMicrophone(micStreamRef.current);
    micStreamRef.current = null;
    isAudioReadyRef.current = false;
  }, []);

  const handleVoiceToggle = useCallback(() => {
    if (isVoiceSessionActive) {
      stopVoiceSession();
      return;
    }
    if (voiceStatus === "connecting") {
      return;
    }
    void startVoiceSession();
  }, [isVoiceSessionActive, startVoiceSession, stopVoiceSession, voiceStatus]);

  const sendBidiTextMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }

      // Mark that user has spoken - now we can process assistant responses
      userHasSpokenRef.current = true;

      const ws = websocketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setVoiceError("Voice session is not connected yet");
        return;
      }
      ws.send(JSON.stringify({ type: "text", text: trimmed }));
      setIsLoading(true);
      const userMessage = createMessage({
        id: `bidi-user-${Date.now()}`,
        role: "user",
        content: trimmed,
        metadata: { source: "bidi" },
      });
      setBidiMessages((prev) => [...prev, userMessage]);
      setInputValue("");
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    isVoiceSessionActiveRef.current = isVoiceSessionActive;
  }, [isVoiceSessionActive]);

  useEffect(() => {
    return () => {
      if (
        websocketRef.current &&
        websocketRef.current.readyState === WebSocket.OPEN
      ) {
        websocketRef.current.close();
      }
      audioPlayerNodeRef.current?.disconnect();
      audioPlayerContextRef.current?.close();
      audioRecorderNodeRef.current?.disconnect();
      audioRecorderContextRef.current?.close();
      stopMicrophone(micStreamRef.current);
      micStreamRef.current = null;
      isAudioReadyRef.current = false;
    };
  }, []);

  const memoizedQuickQuestions = useMemo(() => quickQuestions, [quickQuestions]);

  // Merge and sort messages from both sources (text and voice) by timestamp
  // This ensures all messages persist regardless of voice session state
  const activeMessages = useMemo(() => {
    // Merge both message arrays
    const allMessages = [
      // Convert regular messages to include metadata if not present
      ...messages.map((msg) => ({
        ...msg,
        metadata: msg.metadata || { source: "sse" },
      })),
      // Include bidi messages
      ...bidiMessages,
    ];

    // Remove duplicates based on message ID (in case same message exists in both arrays)
    const uniqueMessages = Array.from(
      new Map(allMessages.map((msg) => [msg.id, msg])).values()
    );

    // Sort by timestamp to maintain chronological order
    return uniqueMessages.sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return timeA - timeB;
    });
  }, [bidiMessages, messages]);

  // Log message state changes for debugging
  useEffect(() => {
    if (isVoiceSessionActive) {
      console.log("[BIDI] bidiMessages state changed", {
        count: bidiMessages.length,
        messages: bidiMessages.map(m => ({
          id: m.id,
          role: m.role,
          contentLength: m.content?.length || 0,
          isProcessing: m.isProcessing,
          hasContent: !!m.content,
          contentPreview: m.content?.substring(0, 50)
        }))
      });
    }
  }, [bidiMessages, isVoiceSessionActive]);

  return {
    // State
    isOpen,
    isMinimized,
    isDark,
    messages: activeMessages,
    inputValue,
    isLoading,
    widgetRef,
    quickQuestions: memoizedQuickQuestions,
    // Bidi/Voice state
    isVoiceSessionActive,
    voiceStatus,
    voiceError,
    // Onboarding state
    onboardingQuestions,
    onboardingActive,
    onboardingCompleted,
    handleOnboardingFormSubmit,
    // Actions
    setInputValue,
    handleSend,
    handleQuickQuestion,
    sendMessage,
    toggleChat,
    toggleTheme,
    toggleMinimize,
    closeChat,
    handleDecision,
    // Bidi/Voice actions
    handleVoiceToggle,
    sendBidiTextMessage,
  };
};
